/**
 * Pure logic for the /calculate view — no React, no DOM.
 * Extracted so it can be unit-tested without a browser or jsdom.
 */
import type { ApiError, GAPriceDto, GAPriceSearchData } from '../api/client.js';

// HTTP 403 Forbidden — treated specially in toErrorMessage because it is not a
// fault (see below). Named so Sonar's magic-number rule has nothing to flag.
const FORBIDDEN_STATUS = 403;

export interface CalculateFormState {
  // Request section
  BusinessType: string;
  OrderQuantity: string;
  L1Price: string;
  RequestId: string;
  // Product identification
  Mlfb: string;
  PriceGroup: string;
  PCKVendor: string;
  GCKVendor: string;
  ListRecognitionMark: string;
  // Customer identification
  CountryCode: string;
  OrgIdBuyer: string;
  ProjectNumber: string;
  CustomerNumber: string;
  CustomerGroup: string;
  GCKBuyer: string;
}

export const DEFAULT_FORM: CalculateFormState = {
  BusinessType: 'N',
  OrderQuantity: '1',
  L1Price: '31271',
  RequestId: '',
  Mlfb: '3AH4305-2ME40-0ED2-Z',
  PriceGroup: '8W1',
  PCKVendor: '4715',
  GCKVendor: 'R3P5',
  ListRecognitionMark: '',
  CountryCode: 'SE',
  OrgIdBuyer: 'A1201456',
  ProjectNumber: '',
  CustomerNumber: '',
  CustomerGroup: '',
  GCKBuyer: '',
};

/** Maps form state → PascalCase GAPriceSearchData DTO. */
export function buildSearchData(form: CalculateFormState): GAPriceSearchData {
  const base: GAPriceSearchData = {
    BusinessType: form.BusinessType,
    OrderQuantity: Number(form.OrderQuantity),
    L1Price: Number(form.L1Price),
    ProductIdentification: {
      Mlfb: form.Mlfb,
      PriceGroup: form.PriceGroup,
      PCKVendor: form.PCKVendor,
      GCKVendor: form.GCKVendor,
      ListRecognitionMark: form.ListRecognitionMark || '',
    },
    CustomerIdentification: {
      CountryCode: form.CountryCode,
      OrgIdBuyer: form.OrgIdBuyer,
      ProjectNumber: form.ProjectNumber || '',
      CustomerNumber: form.CustomerNumber || '',
      CustomerGroup: form.CustomerGroup || '',
      GCKBuyer: form.GCKBuyer || '',
    },
  };

  if (form.RequestId.trim() !== '') {
    base.RequestId = form.RequestId.trim();
  }

  return base;
}

/**
 * Returns a map of fieldKey → error message for any validation failures.
 * An empty object means the form is valid.
 */
export function validateForm(form: CalculateFormState): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.BusinessType.trim()) {
    errors['BusinessType'] = 'Business type is required';
  }

  const qty = Number(form.OrderQuantity);
  if (!form.OrderQuantity.trim() || !Number.isInteger(qty) || qty < 1) {
    errors['OrderQuantity'] = 'Order quantity must be a positive integer';
  }

  const price = Number(form.L1Price);
  if (!form.L1Price.trim() || isNaN(price) || price <= 0) {
    errors['L1Price'] = 'L1 price must be greater than 0';
  }

  if (!form.Mlfb.trim()) errors['Mlfb'] = 'MLFB is required';
  if (!form.PriceGroup.trim()) errors['PriceGroup'] = 'Price group is required';
  if (!form.PCKVendor.trim()) errors['PCKVendor'] = 'PCK vendor is required';
  if (!form.GCKVendor.trim()) errors['GCKVendor'] = 'GCK vendor is required';

  if (!form.CountryCode.trim()) errors['CountryCode'] = 'Country code is required';
  if (!form.OrgIdBuyer.trim()) errors['OrgIdBuyer'] = 'Org ID buyer is required';

  return errors;
}

/**
 * Interprets the DTO logical outcome. On success the component renders the DTO
 * fields directly, so the success branch carries no message.
 */
export function interpretResult(
  dto: GAPriceDto,
): { ok: true } | { ok: false; message: string } {
  if (dto.ResponseStatus === '200') {
    return { ok: true };
  }

  const status = dto.ResponseStatus ?? 'unknown';
  const detail = dto.ResponseMessage ?? 'No details provided';
  return { ok: false, message: `Error ${status}: ${detail}` };
}

/**
 * Returns className + (conditionally) invalidText props for a field.
 * `invalidText` is omitted entirely (never undefined) when there's no error,
 * as required by exactOptionalPropertyTypes. Keying by `keyof CalculateFormState`
 * makes a mistyped field name a compile-time error.
 */
export function fieldProps(errors: Record<string, string>, key: keyof CalculateFormState) {
  const msg = errors[key];
  return msg
    ? { className: 'ix-invalid' as const, invalidText: msg }
    : { className: '' as const };
}

/**
 * Structural check rather than `err instanceof ApiError`.
 *
 * ApiError can only be imported as a value from ../api/client.js, and that
 * module configures the generated OpenAPI singleton, which pulls in MSAL and
 * dereferences `window` at import time. This module is deliberately DOM-free so
 * it can be unit-tested under the node environment (see the note at the top of
 * calculate.logic.test.ts), and client.ts states that nothing may import from
 * the generated subtree directly. A type-only import plus a shape check keeps
 * both constraints. ApiError extends Error and carries a numeric `status`.
 */
function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && typeof (err as { status?: unknown }).status === 'number';
}

/**
 * Turns a thrown request error into text a user can act on.
 *
 * 403 gets its own message because it is not a fault: the BFF and ga-calc
 * require the `tps.read` application role (packages/auth/roles.ts) and the
 * gateway does not check roles, so a signed-in user with no TPS entitlement
 * reaches the calculator and is refused here. "Request failed (HTTP 403):
 * Forbidden" reads like a bug and sends people to the wrong team.
 */
export function toErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === FORBIDDEN_STATUS) {
      return (
        'You are signed in, but your account is not authorised to use transfer pricing. ' +
        'Ask an administrator to assign you the TPS reader role.'
      );
    }
    return `Request failed (HTTP ${err.status}): ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'An unexpected error occurred';
}
